import { useState, useEffect, useContext, useCallback, useRef } from "react";
import firebase from "../../firebase/compat";
import PageHeader from "../../components/layout/PageHeader";
import TinyMCE from "../../components/common/TinyMCE";
import AudioRecorderTab from "../../components/storytelling/interviewer/AudioRecorderTab";
import AudioUploadTab from "../../components/storytelling/interviewer/AudioUploadTab";
import DocumentUploadTab from "../../components/storytelling/interviewer/DocumentUploadTab";
import MechanismProposalList from "../../components/storytelling/MechanismProposalList";
import useMechanismProposals from "../../components/storytelling/useMechanismProposals";
import { useFirestoreID, useFirestore, useFirestoreGeneralArrayContains } from "../../firebase/useFirestore";
import Location from "../../hooks/Location";
import { client } from "../../hooks/Client";
import { db, timestamp } from "../../firebase/config";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import saveQuoteAnecdoteProposals from "../../helpers/storytellingHelpers/saveQuoteAnecdoteProposals";
import buildAcceptedPayload, { NEW_ENTITY_VALUE } from "../../helpers/mechanisms/buildAcceptedPayload";
import spinner from "../../images/spinner-ripple.svg";

/**
 * Interview detail + unified AI review. After the transcript is saved (post
 * privacy-pipeline) the AI proposes EVERYTHING at once — quotes, anecdotes,
 * werkzame mechanismen and the Theory-of-Change couplings. Nothing is written
 * until the user reviews and clicks one save button. Re-running the analysis
 * later is a manual button (it costs an AI call), not automatic on every visit.
 */
const StorytellingInterviewDetail = () => {
    // Context
    const [, setSaved] = useContext(SavedIcon);

    // Transcript state
    const [transcript, setTranscript] = useState("");
    const [docid, setDocid] = useState("");
    const [loading, setLoading] = useState(false);
    const [interviewType, setInterviewType] = useState("deelnemer");
    const [audioTab, setAudioTab] = useState("audio");
    const [audioPhase, setAudioPhase] = useState("idle");

    // Unified review state. Everything is a PROPOSAL — nothing is persisted until
    // the user clicks "Voorstellen opslaan".
    const [analysisStatus, setAnalysisStatus] = useState("idle"); // idle | running | review | saving | done | error
    const [quoteProposals, setQuoteProposals] = useState([]); // [{ text, accepted }]
    const [anecdoteProposals, setAnecdoteProposals] = useState([]);
    const [tocActivities, setTocActivities] = useState([]); // [{ label, accepted }]
    const [tocTargetgroups, setTocTargetgroups] = useState([]);
    const [tocEffects, setTocEffects] = useState([]); // [{ raw, effect, evidence, explanation, accepted }]
    const [tags, setTags] = useState([]);
    const [samenvatting, setSamenvatting] = useState("");
    const [keepSummary, setKeepSummary] = useState(true);
    const [evidencePassages, setEvidencePassages] = useState([]);
    const [savedSummary, setSavedSummary] = useState("");
    const [analysisError, setAnalysisError] = useState("");

    // Hooks
    const interviewId = Location()[3];

    // Mechanism proposals (auto-started as part of the analysis, saved on confirm).
    const { proposals: mechanismProposals, status: mechStatus, propose: proposeMechanisms, save: saveMechanisms } =
        useMechanismProposals(interviewId);
    const [mechanismRows, setMechanismRows] = useState([]);

    // Which save steps already succeeded, so a retry after a mid-save failure
    // skips them — none of the three writers dedupe, so re-running would create
    // duplicate quotes/anecdotes. Reset on every fresh analysis.
    const savedStepsRef = useRef({ quotes: false, mechanisms: false, analysis: false });

    // Firestore
    const interviews = useFirestoreID("StorytellingInterviews", interviewId || "");
    const interview = interviews && interviews[0];

    const stakeholders = useFirestore("Stakeholders") || [];
    const activitiesList = useFirestore("Activities") || [];
    const effectsList = useFirestore("OutputEffects") || [];
    const msiEffects = effectsList.filter((e) => e.Standard === "true" || e.MSIId);

    // Already-saved state (shown read-only when not actively reviewing).
    const relatedActivities = (interview && interview.RelatedActivities) || [];
    const relatedEffects = (interview && interview.RelatedEffects) || [];
    const tocMechanisms =
        useFirestoreGeneralArrayContains("TocMechanisms", "SourceInterviewIDs", interviewId || "") || [];
    const savedMechanisms = tocMechanisms.filter((m) => !m.Deleted);
    const activityName = (id) => (activitiesList.find((a) => a.ID === id) || {}).Activity || "";
    const effectName = (id) => (effectsList.find((e) => e.ID === id) || {}).Effect || "";
    const interviewEvidences = (m) =>
        (m.Sources || [])
            .filter((s) => s.InterviewID === interviewId && s.Evidence && !s.Deleted)
            .map((s) => s.Evidence);

    // Load the redacted transcript into editable state
    useEffect(() => {
        if (interview) {
            setTranscript(interview.AudioTranscript || "");
            setDocid(interview.docid);
            setInterviewType(interview.Type || "deelnemer");
        }
    }, [interview]);

    // Seed the editable mechanism rows when Decca's proposals arrive. A proposed
    // NEW doelgroep/activiteit pre-selects the "nieuw" option.
    useEffect(() => {
        setMechanismRows(
            mechanismProposals.map((p) => ({
                label: p.title,
                category: p.category || "",
                mechanismId: p.mechanismId || null,
                evidence: p.evidence,
                explanation: p.explanation || "",
                targetgroupId: p.suggestedTargetgroupId || (p.newTargetgroupLabel ? NEW_ENTITY_VALUE : ""),
                newTargetgroupLabel: p.newTargetgroupLabel || "",
                activityId: p.suggestedActivityId || (p.newActivityLabel ? NEW_ENTITY_VALUE : ""),
                newActivityLabel: p.newActivityLabel || "",
                effectId: p.suggestedEffectId || "",
                isNew: p.isNew,
                accepted: true,
            }))
        );
    }, [mechanismProposals]);

    // Who is being interviewed drives the AI analysis framing.
    // AI-CONTEXT: See docs/features/insight-source-citations.md
    const handleTypeChange = (type) => {
        setInterviewType(type);
        if (interview?.docid) {
            db.collection("StorytellingInterviews").doc(interview.docid).update({
                Type: type,
                TypeConfirmed: true,
            });
        }
    };

    const hasTranscript = Boolean(interview && interview.AudioTranscript);

    // Fill the review from a (read-only) analysis result and kick off the
    // mechanism proposals so everything lands in one review.
    const populateReview = (result) => {
        setQuoteProposals((result?.BelangrjkeQuotes || []).map((text) => ({ text, accepted: true })));
        setAnecdoteProposals((result?.Anekdotes || []).map((text) => ({ text, accepted: true })));
        // Sinds 2026-09-14 zijn de koppelingen objecten met een id ({activity,
        // activityId}); een oudere analyse gaf kale labels. `raw` gaat bij het
        // opslaan terug, zodat het id niet verloren gaat.
        setTocActivities((result?.RelatedActivities || []).map((item) => ({
            raw: item, label: typeof item === "string" ? item : item.activity, accepted: true,
        })));
        setTocTargetgroups((result?.RelatedTargetgroups || []).map((item) => ({
            raw: item, label: typeof item === "string" ? item : item.targetgroup, accepted: true,
        })));
        setTocEffects(
            (result?.RelatedEffects || []).map((item) => ({
                raw: item,
                effect: typeof item === "string" ? item : item.effect,
                evidence: typeof item === "string" ? "" : item.evidence,
                explanation: typeof item === "string" ? "" : item.explanation,
                accepted: true,
            }))
        );
        setTags(result?.Tags || []);
        setSamenvatting(result?.Samenvatting || "");
        setKeepSummary(true);
        setEvidencePassages(result?.EvidencePassages || []);
        savedStepsRef.current = { quotes: false, mechanisms: false, analysis: false };
        proposeMechanisms();
        setAnalysisStatus("review");
    };

    // Audio tabs report their phase; when the transcript save starts, show the
    // running state. useCallback keeps identity stable across this page's
    // Firestore listeners re-rendering.
    const handleAudioPhaseChange = useCallback((phase) => {
        setAudioPhase(phase);
        if (phase === "saving") {
            setAnalysisStatus("running");
            setAnalysisError("");
        }
    }, []);

    // Initial analysis result comes up from the tab (which just saved the
    // transcript + ran the read-only analysis).
    const handleAnalysisComplete = (result) => populateReview(result);

    const handleAnalysisError = (message) => {
        setAnalysisError(message || "");
        setAnalysisStatus("error");
    };

    // Re-run the analysis later (the tab is gone once a transcript exists). Calls
    // the read-only analysis directly, then repopulates the review.
    const reAnalyze = async () => {
        setAnalysisStatus("running");
        setAnalysisError("");
        try {
            const fn = firebase.app().functions("europe-west1").httpsCallable("generateStorytellingIndexCompagnion");
            const res = await fn({
                text: interview.AudioTranscript,
                interviewId,
                compagnyId: client,
                type: interview.Type || "deelnemer",
                persist: false,
            });
            populateReview(res.data);
        } catch (err) {
            console.error(err);
            setAnalysisError(err.message || "");
            setAnalysisStatus("error");
        }
    };

    // Generic accept/reject toggler for a proposal list.
    const toggleAt = (setter) => (index) =>
        setter((prev) => prev.map((x, i) => (i === index ? { ...x, accepted: !x.accepted } : x)));
    const toggleQuote = toggleAt(setQuoteProposals);
    const toggleAnecdote = toggleAt(setAnecdoteProposals);
    const toggleTocActivity = toggleAt(setTocActivities);
    const toggleTocTargetgroup = toggleAt(setTocTargetgroups);
    const toggleTocEffect = toggleAt(setTocEffects);
    const updateMechanismRow = (index, patch) =>
        setMechanismRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

    const summarize = ({ quotes, anecdotes, mechanisms, couplings }) => {
        const parts = [];
        if (quotes) parts.push(`${quotes} ${quotes === 1 ? "quote" : "quotes"}`);
        if (anecdotes) parts.push(`${anecdotes} ${anecdotes === 1 ? "anekdote" : "anekdotes"}`);
        if (mechanisms) parts.push(`${mechanisms} ${mechanisms === 1 ? "werkzaam element" : "werkzame elementen"}`);
        if (couplings) parts.push(`${couplings} TOC-koppeling${couplings === 1 ? "" : "en"}`);
        return parts.length === 0 ? "Niets opgeslagen." : `${parts.join(", ")} opgeslagen.`;
    };

    // ONE save: writes every accepted proposal across all groups.
    const saveAll = async () => {
        setAnalysisStatus("saving");
        setAnalysisError("");

        const quotes = quoteProposals.filter((q) => q.accepted).map((q) => q.text);
        const anekdotes = anecdoteProposals.filter((a) => a.accepted).map((a) => a.text);
        const acceptedMechanisms = buildAcceptedPayload(mechanismRows);
        const acceptedActivities = tocActivities.filter((a) => a.accepted).map((a) => a.raw ?? a.label);
        const acceptedTargetgroups = tocTargetgroups.filter((t) => t.accepted).map((t) => t.raw ?? t.label);
        const acceptedEffects = tocEffects.filter((e) => e.accepted).map((e) => e.raw);

        try {
            // Each step runs once; a retry after a mid-save failure skips the
            // steps that already succeeded (no writer dedupes).
            if (!savedStepsRef.current.quotes) {
                await saveQuoteAnecdoteProposals({
                    db,
                    client,
                    timestamp,
                    interviewName: interview?.Title || "",
                    quotes,
                    anekdotes,
                });
                savedStepsRef.current.quotes = true;
            }

            if (!savedStepsRef.current.mechanisms) {
                if (acceptedMechanisms.length > 0) {
                    await saveMechanisms(acceptedMechanisms);
                }
                savedStepsRef.current.mechanisms = true;
            }

            if (!savedStepsRef.current.analysis) {
                const saveAnalysis = firebase.app().functions("europe-west1").httpsCallable("saveStorytellingAnalysis");
                await saveAnalysis({
                    interviewId,
                    callerCompanyId: client,
                    accepted: {
                        relatedActivities: acceptedActivities,
                        relatedTargetgroups: acceptedTargetgroups,
                        relatedEffects: acceptedEffects,
                        tags: keepSummary ? tags : [],
                        samenvatting: keepSummary ? samenvatting : "",
                        evidencePassages,
                    },
                });
                savedStepsRef.current.analysis = true;
            }

            setSavedSummary(
                summarize({
                    quotes: quotes.length,
                    anecdotes: anekdotes.length,
                    mechanisms: acceptedMechanisms.length,
                    couplings: acceptedActivities.length + acceptedTargetgroups.length + acceptedEffects.length,
                })
            );
            setAnalysisStatus("done");
        } catch (err) {
            console.error(err);
            // Back to the review (not a full re-analysis) so the user can retry
            // the save — completed steps are skipped, so nothing is duplicated.
            setAnalysisError(err.message || "");
            setAnalysisStatus("review");
        }
    };

    // Persist edits back to the interview document
    const saveTranscript = () => {
        setLoading(true);
        db.collection("StorytellingInterviews")
            .doc(docid)
            .update({ AudioTranscript: transcript })
            .then(() => {
                setSaved(true);
                setLoading(false);
            });
    };

    const hasAnyProposal =
        quoteProposals.length > 0 ||
        anecdoteProposals.length > 0 ||
        tocActivities.length > 0 ||
        tocTargetgroups.length > 0 ||
        tocEffects.length > 0 ||
        mechanismRows.length > 0 ||
        mechStatus === "proposing";

    const hasSavedState = savedMechanisms.length > 0 || relatedActivities.length > 0 || relatedEffects.length > 0;

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={interview?.Title || "Interview detail"}
                    description={"Bekijk en bewerk de geredigeerde transcriptie van dit interview."}
                    AI={false}
                />

                {interview && !hasTranscript && (
                    <div className="dashboard-container">
                        {audioPhase === "idle" && (
                            <>
                                <div className="interview-type-toggle">
                                    <p className="interview-type-label">Wie wordt geïnterviewd?</p>
                                    <div className="interview-type-options">
                                        <button
                                            className={
                                                interviewType === "deelnemer"
                                                    ? "interview-type-option interview-type-option-active"
                                                    : "interview-type-option"
                                            }
                                            onClick={() => handleTypeChange("deelnemer")}
                                        >
                                            Deelnemer (doelgroep)
                                        </button>
                                        <button
                                            className={
                                                interviewType === "begeleider"
                                                    ? "interview-type-option interview-type-option-active"
                                                    : "interview-type-option"
                                            }
                                            onClick={() => handleTypeChange("begeleider")}
                                        >
                                            Begeleider / eigenaar
                                        </button>
                                    </div>
                                    <p className="interview-type-hint">
                                        {interviewType === "begeleider"
                                            ? "De analyse richt zich op waaróm effecten ontstaan bij deelnemers, vanuit de begeleider."
                                            : "De analyse richt zich op de eigen ervaring en effecten van deze deelnemer."}
                                    </p>
                                </div>
                                <h2>Interview toevoegen</h2>
                                <div className="interview-audio-actions">
                                    <button
                                        className={
                                            audioTab === "audio"
                                                ? "interview-audio-tab interview-audio-tab-active"
                                                : "interview-audio-tab"
                                        }
                                        onClick={() => setAudioTab("audio")}
                                    >
                                        Opnemen
                                    </button>
                                    <button
                                        className={
                                            audioTab === "upload"
                                                ? "interview-audio-tab interview-audio-tab-active"
                                                : "interview-audio-tab"
                                        }
                                        onClick={() => setAudioTab("upload")}
                                    >
                                        Audio uploaden
                                    </button>
                                    <button
                                        className={
                                            audioTab === "document"
                                                ? "interview-audio-tab interview-audio-tab-active"
                                                : "interview-audio-tab"
                                        }
                                        onClick={() => setAudioTab("document")}
                                    >
                                        Document uploaden
                                    </button>
                                </div>
                            </>
                        )}
                        {audioTab === "audio" && (
                            <AudioRecorderTab
                                interview={interview}
                                onPhaseChange={handleAudioPhaseChange}
                                onAnalysisComplete={handleAnalysisComplete}
                                onAnalysisError={handleAnalysisError}
                            />
                        )}
                        {audioTab === "upload" && (
                            <AudioUploadTab
                                interview={interview}
                                onPhaseChange={handleAudioPhaseChange}
                                onAnalysisComplete={handleAnalysisComplete}
                                onAnalysisError={handleAnalysisError}
                            />
                        )}
                        {audioTab === "document" && (
                            <DocumentUploadTab
                                interview={interview}
                                onPhaseChange={handleAudioPhaseChange}
                                onAnalysisComplete={handleAnalysisComplete}
                                onAnalysisError={handleAnalysisError}
                            />
                        )}
                    </div>
                )}

                {interview && (
                    <div className="dashboard-container">
                        <h2>Transcriptie</h2>
                        {interview.AudioTranscript ? (
                            <>
                                <TinyMCE body={transcript} setBody={setTranscript} />
                                {loading ? (
                                    <img src={spinner} alt="loading spinner" />
                                ) : (
                                    <button className="button-simple" onClick={saveTranscript}>
                                        Opslaan
                                    </button>
                                )}
                            </>
                        ) : (
                            <p>Geen transcriptie beschikbaar.</p>
                        )}
                    </div>
                )}

                {analysisStatus === "running" && (
                    <div className="dashboard-container">
                        <div className="interview-analysis-status">
                            <span className="privacy-step-spinner" />
                            <p>Decca analyseert het interview en stelt alles in één keer voor…</p>
                        </div>
                    </div>
                )}

                {(analysisStatus === "review" || analysisStatus === "saving") && (
                    <div className="dashboard-container">
                        <h2>Voorstellen uit dit interview</h2>
                        <p>
                            Decca stelt dit voor uit het interview. Vink aan wat je wilt bewaren en pas
                            waar nodig aan. Er wordt niets opgeslagen tot je onderaan op bewaren klikt.
                        </p>

                        {samenvatting && (
                            <div className="interview-proposal-group">
                                <h3 className="interview-proposal-heading">Samenvatting</h3>
                                <p className="interview-summary-text">{samenvatting}</p>
                                <label className="interview-proposal-item">
                                    <input
                                        type="checkbox"
                                        checked={keepSummary}
                                        onChange={() => setKeepSummary((v) => !v)}
                                    />
                                    <span>Samenvatting{tags.length > 0 ? " en tags" : ""} bewaren</span>
                                </label>
                            </div>
                        )}

                        {quoteProposals.length > 0 && (
                            <div className="interview-proposal-group">
                                <h3 className="interview-proposal-heading">Quotes</h3>
                                {quoteProposals.map((q, index) => (
                                    <label className="interview-proposal-item" key={index}>
                                        <input type="checkbox" checked={q.accepted} onChange={() => toggleQuote(index)} />
                                        <blockquote className="interview-proposal-text">{q.text}</blockquote>
                                    </label>
                                ))}
                            </div>
                        )}

                        {anecdoteProposals.length > 0 && (
                            <div className="interview-proposal-group">
                                <h3 className="interview-proposal-heading">Anekdotes</h3>
                                {anecdoteProposals.map((a, index) => (
                                    <label className="interview-proposal-item" key={index}>
                                        <input type="checkbox" checked={a.accepted} onChange={() => toggleAnecdote(index)} />
                                        <blockquote className="interview-proposal-text">{a.text}</blockquote>
                                    </label>
                                ))}
                            </div>
                        )}

                        <div className="interview-proposal-group">
                            <h3 className="interview-proposal-heading">Werkzame mechanismen</h3>
                            {mechStatus === "proposing" ? (
                                <div className="interview-analysis-status">
                                    <span className="privacy-step-spinner" />
                                    <p>Decca stelt werkzame mechanismen voor…</p>
                                </div>
                            ) : mechanismRows.length > 0 ? (
                                <MechanismProposalList
                                    rows={mechanismRows}
                                    updateRow={updateMechanismRow}
                                    targetgroups={stakeholders}
                                    activities={activitiesList}
                                    msiEffects={msiEffects}
                                />
                            ) : (
                                <p className="interview-matches-empty">Geen werkzame mechanismen gevonden.</p>
                            )}
                        </div>

                        {(tocActivities.length > 0 || tocTargetgroups.length > 0 || tocEffects.length > 0) && (
                            <div className="interview-proposal-group">
                                <h3 className="interview-proposal-heading">Gekoppeld aan de Theory of Change</h3>

                                {tocActivities.length > 0 && (
                                    <div className="interview-matches-group">
                                        <h4 className="interview-matches-heading">Activiteiten</h4>
                                        {tocActivities.map((a, index) => (
                                            <label className="interview-proposal-item" key={index}>
                                                <input type="checkbox" checked={a.accepted} onChange={() => toggleTocActivity(index)} />
                                                <span className="interview-match-chip">{a.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                {tocTargetgroups.length > 0 && (
                                    <div className="interview-matches-group">
                                        <h4 className="interview-matches-heading">Doelgroepen</h4>
                                        {tocTargetgroups.map((t, index) => (
                                            <label className="interview-proposal-item" key={index}>
                                                <input type="checkbox" checked={t.accepted} onChange={() => toggleTocTargetgroup(index)} />
                                                <span className="interview-match-chip">{t.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                {tocEffects.length > 0 && (
                                    <div className="interview-matches-group">
                                        <h4 className="interview-matches-heading">Effecten</h4>
                                        {tocEffects.map((e, index) => (
                                            <label className="interview-proposal-item" key={index}>
                                                <input type="checkbox" checked={e.accepted} onChange={() => toggleTocEffect(index)} />
                                                <div className="interview-effect">
                                                    <span className="interview-match-chip">{e.effect}</span>
                                                    {e.evidence && (
                                                        <blockquote className="interview-effect-quote">{e.evidence}</blockquote>
                                                    )}
                                                    {e.explanation && <p className="interview-effect-why">{e.explanation}</p>}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="interview-proposal-actions">
                            {analysisError && (
                                <p className="interview-analysis-error">
                                    Opslaan is deels mislukt: {analysisError}. Klik nogmaals op
                                    'Voorstellen opslaan' — al bewaarde onderdelen worden overgeslagen.
                                </p>
                            )}
                            {analysisStatus === "saving" ? (
                                <img src={spinner} alt="loading spinner" />
                            ) : (
                                <button className="button-simple" onClick={saveAll} disabled={!hasAnyProposal}>
                                    Voorstellen opslaan
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {analysisStatus === "error" && (
                    <div className="dashboard-container">
                        <p className="interview-analysis-error">De analyse is mislukt: {analysisError}</p>
                        {hasTranscript && (
                            <button className="button-simple" onClick={reAnalyze}>
                                Opnieuw proberen
                            </button>
                        )}
                    </div>
                )}

                {hasTranscript && (analysisStatus === "idle" || analysisStatus === "done") && (
                    <div className="dashboard-container">
                        <h2>Voorstellen uit het interview</h2>
                        {analysisStatus === "done" && <p className="interview-analysis-done">✓ {savedSummary}</p>}
                        <p>
                            Laat Decca dit interview omzetten in quotes, anekdotes, werkzame mechanismen
                            en koppelingen met je Theory of Change. Je controleert alles voordat er iets
                            wordt opgeslagen.
                        </p>
                        <button className="button-simple" onClick={reAnalyze}>
                            {hasSavedState || analysisStatus === "done" ? "Opnieuw analyseren" : "Interview analyseren"}
                        </button>

                        {hasSavedState && (
                            <div className="interview-saved-state">
                                {savedMechanisms.length > 0 && (
                                    <div className="mechanism-interview-list">
                                        <h3 className="interview-matches-heading">Opgeslagen werkzame mechanismen</h3>
                                        {savedMechanisms.map((m) => (
                                            <div className="mechanism-interview-item" key={m.ID}>
                                                <div className="mechanism-interview-head">
                                                    <span className="mechanism-overview-label">{m.Title}</span>
                                                    {(m.Activity || []).map((activityId) => (
                                                        <span className="interview-match-chip" key={activityId}>
                                                            {activityName(activityId)}
                                                        </span>
                                                    ))}
                                                    {(m.Effect || []).map((effectId) => (
                                                        <span className="mechanism-overview-effect-chip" key={effectId}>
                                                            {effectName(effectId)}
                                                        </span>
                                                    ))}
                                                </div>
                                                {interviewEvidences(m).map((evidence, i) => (
                                                    <blockquote className="mechanism-review-evidence" key={i}>
                                                        {evidence}
                                                    </blockquote>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {(relatedActivities.length > 0 || relatedEffects.length > 0) && (
                                    <div className="interview-matches-group">
                                        <h3 className="interview-matches-heading">Gekoppeld aan de Theory of Change</h3>
                                        {relatedActivities.length > 0 && (
                                            <div className="interview-match-chips">
                                                {relatedActivities.map((item, index) => (
                                                    <span className="interview-match-chip" key={index}>
                                                        {typeof item === "string" ? item : item.activity}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {relatedEffects.length > 0 && (
                                            <div className="interview-effect-list">
                                                {relatedEffects.map((item, index) => {
                                                    const effect = typeof item === "string" ? item : item.effect;
                                                    return (
                                                        <span className="interview-match-chip" key={index}>
                                                            {effect}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StorytellingInterviewDetail;
